import * as amplitude from "@amplitude/analytics-browser";
import type { AnalyticsPort, CommonProps } from "./types";

/**
 * 웹 Amplitude. 자동 수집은 sessions 만, 세션 타임아웃 5분(앱 SDK 와 같게).
 * enrichment 플러그인이 공통 프로퍼티를 모든 이벤트(자동 session_start 포함)에 병합하고, session_start 를 세션 신호로 바꾼다.
 */
let ready = false;
let common: CommonProps | null = null;
let pendingSessionStarts = 0;
const sessionListeners = new Set<() => void>();

const emitSessionStart = () => {
  if (sessionListeners.size === 0) pendingSessionStarts += 1;
  else for (const cb of [...sessionListeners]) cb();
};

const enrichment: amplitude.Types.EnrichmentPlugin = {
  name: "hamlak-common",
  type: "enrichment",
  async setup() {},
  async execute(event) {
    if (common) event.event_properties = { ...common, ...event.event_properties };
    if (event.event_type === "session_start") emitSessionStart();
    return event;
  },
};

export const webAnalytics: AnalyticsPort = {
  async init({ apiKey, deviceId, commonProps }) {
    if (ready) return;
    ready = true;
    common = commonProps;
    amplitude.add(enrichment);
    await amplitude
      .init(apiKey, {
        deviceId,
        sessionTimeout: 5 * 60_000,
        identityStorage: "localStorage",
        autocapture: { sessions: true, attribution: false, pageViews: false, formInteractions: false, fileDownloads: false, elementInteractions: false },
        flushIntervalMillis: 5000,
        flushQueueSize: 10,
      })
      .promise.catch(() => undefined);
  },
  setCommonProps(props) {
    common = props;
  },
  track(event, props) {
    if (!ready) return;
    amplitude.track(event, props);
  },
  setUserId(userId) {
    if (!ready) return;
    amplitude.setUserId(userId ?? undefined);
  },
  setUserProperties({ set, setOnce }) {
    if (!ready) return;
    const id = new amplitude.Identify();
    for (const [k, v] of Object.entries(set ?? {})) id.set(k, v as never);
    for (const [k, v] of Object.entries(setOnce ?? {})) id.setOnce(k, v as never);
    amplitude.identify(id);
  },
  onSessionStart(cb) {
    sessionListeners.add(cb);
    for (; pendingSessionStarts > 0; pendingSessionStarts -= 1) cb();
    return () => sessionListeners.delete(cb);
  },
  async getInstallReferrer() {
    return null;
  },
  async flush() {
    if (!ready) return;
    await amplitude.flush().promise.catch(() => undefined);
  },
};
